import dayjs, { Dayjs } from 'dayjs';
import { getAgeCategory, AgeCategory, dateToString } from 'helpers/date';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useStore } from 'providers';
import { OnboardingSlideProps } from 'models';
import { StyledTextField } from './DateOfBirth.styles';

const dobErrorMessage: Record<Exclude<AgeCategory, 'ok'>, string> = {
  high: 'Wow, congrats on being +120 years old. Unfortunately, Zillionaires need to be 119 and under.',
  low: `Oops, looks like you're too young to sign up. Zillionaires need to be 16 and over.`,
};

const DateOfBirth = ({
  onboardingData: { surveyAnswers, buttonState },
  setOnboardingData,
}: OnboardingSlideProps) => {
  const { store } = useStore();
  const { surveyQuestions } = store;

  const {
    DOB: { questionID },
  } = surveyQuestions || {};

  const dayToday = dayjs();

  const newDateOfBirth = surveyAnswers?.answer2?.value;
  const dateOfBirth = newDateOfBirth ? dayjs(newDateOfBirth) : null;
  const ageCategory = getAgeCategory(dateOfBirth);
  const isAgeError = ageCategory !== 'ok';
  const showError = dateOfBirth?.isValid() && isAgeError;
  const showHelperText = showError && dobErrorMessage[ageCategory];

  const setButtonState = (date: Dayjs | null) => {
    const newAgeCategory = getAgeCategory(date);
    const isError = !date?.isValid() || newAgeCategory !== 'ok';
    const updatedButtonState = { ...buttonState, dateOfBirth: isError };

    setOnboardingData({
      type: 'setButtonState',
      data: { buttonState: updatedButtonState },
    });
  };

  const handleOnChange = (date: Dayjs | null) => {
    setButtonState(date);

    const value = date ? dateToString(date) : undefined;
    const answer2 = { questionID, value };

    setOnboardingData({
      type: 'setSurveyAnswers',
      data: { surveyAnswers: { ...surveyAnswers, answer2 } },
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        value={dateOfBirth}
        onChange={handleOnChange}
        inputFormat="DD/MM/YYYY"
        maxDate={dayToday}
        views={['year', 'month', 'day']}
        openTo="year"
        renderInput={(params) => (
          <StyledTextField
            {...params}
            fullWidth
            placeholder="DD/MM/YYYY"
            label="Enter your birthday"
            variant="filled"
            error={showError}
            InputLabelProps={{ shrink: true }}
            helperText={showHelperText}
          />
        )}
      />
    </LocalizationProvider>
  );
};

export default DateOfBirth;
